// interactiveItemService — the studio half of the Interactive Item feature.
//
// Four calls, three cost classes:
//
//   fetchInteractiveCatalog()  free, cached. The 23 activity types and the 10
//                              data shapes. The editor does NOT hardcode the
//                              list: a new type ships as a backend row and
//                              appears here without a studio release.
//   generateInteractive()      expensive, queued. Returns a READY BOX — the
//                              plugin, the state, the data and (for scored
//                              types) the exercise record — the way the scene
//                              visual tabs do.
//   estimateInteractive()      what this click will cost.

import { saveAs } from "file-saver";
import { getToken } from "../../../common/environment_config.es6";
import { waitForJob } from "../../../common/job_poll.es6";

/**
 * The model picker. "" means "let the backend choose" — deliberately not a
 * model id pinned here: the default lives in one place
 * (DEFAULT_INTERACTIVE_MODEL) and a copy in the studio would drift from it
 * and, because the price is per model, quietly bill against the wrong row.
 */
export const INTERACTIVE_MODELS = [
  { value: "", labelKey: "InteractiveItem.ModelAuto", fallback: "Otomatik (Sonnet 5.5)" },
  { value: "claude-sonnet-5.5", labelKey: "InteractiveItem.ModelSonnet", fallback: "Sonnet 5.5" },
  { value: "claude-opus-5.5", labelKey: "InteractiveItem.ModelOpus", fallback: "Opus 5.5" },
];

/**
 * Hazır ana renkler — ilki aidea'nın moru ve VARSAYILAN olarak seçili gelir.
 *
 * Neden burada ve neden sadece hex: bunlar yalnızca seçicinin kareleri.
 * Sunucuya giden şey yine tek bir renk ve paleti ondan TÜRETEN tarif
 * (InteractiveStyleService) — yani on kare, on ayrı "tema" değil, on
 * kısayol. Bir ara her biri kendi tarifini taşıyan bir palet kataloğu
 * denendi; gereksiz ağırlıktı ve rengi kullanıcıdan almanın önüne geçiyordu.
 *
 * Varsayılanın BOŞ olmaması bilinçli: boş bırakmak, yazarı her seferinde renk
 * seçmeye zorluyordu ve kimse markasından başka bir şey istemiyorsa o zorlama
 * sadece bir adım fazlalık. Marka rengi bilinçli bir varsayılan — sessizce
 * okunan bir kurum satırı değil, ekranda seçili duran ve tek tıkla
 * değiştirilebilen bir kare.
 */
export const INTERACTIVE_COLORS = [
  { value: "#7C3AED", label: "aidea" },
  { value: "#2563EB", label: "Mavi" },
  { value: "#0EA5E9", label: "Gök" },
  { value: "#0D9488", label: "Turkuaz" },
  { value: "#059669", label: "Yeşil" },
  { value: "#CA8A04", label: "Hardal" },
  { value: "#EA580C", label: "Turuncu" },
  { value: "#E11D48", label: "Kırmızı" },
  { value: "#DB2777", label: "Pembe" },
  { value: "#0F172A", label: "Gece" },
];

/** Hiçbir şey seçilmediğinde kullanılan renk — listenin ilki. */
export const DEFAULT_INTERACTIVE_COLOR = INTERACTIVE_COLORS[0].value;

function apiBase() {
  const base = process.env.MW_API_URL || "";
  return base.endsWith("/") ? base : `${base}/`;
}

/**
 * The editor's ?id= is the Ideal Studio training UUID — the socket room the
 * job widget joined and the blob scope the document is written under. Absent
 * in standalone mode, where the document simply lands in the org's temp scope.
 */
function roomTrainingId() {
  try {
    return new URLSearchParams(window.location.search).get("id") || null;
  } catch {
    return null;
  }
}

function buildForm(fields) {
  const form = new FormData();
  const put = (key, value) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    form.append(key, String(value));
  };

  put("typeId", fields.typeId);
  put("outline", fields.outline);
  put("accentColor", fields.accentColor);
  put("instruction", fields.instruction);
  put("sourceBlobName", fields.sourceBlobName);
  put("currentItem", fields.currentItem);
  put("prompt", fields.prompt);
  put("narrationText", fields.narrationText);
  put("navId", fields.navId);
  put("sceneTitle", fields.sceneTitle);
  put("styleBrief", fields.styleBrief);
  put("contentLanguage", fields.contentLanguage);
  put("sceneIndex", fields.sceneIndex);
  put("llmModel", fields.llmModel);
  put("trainingId", fields.trainingId);

  const roomId = roomTrainingId();
  if (roomId) {
    form.append("idealStudioTrainingId", roomId);
  }

  return form;
}

async function post(path, fields) {
  const response = await fetch(`${apiBase()}api/interactive/${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: buildForm(fields),
  });
  if (!response.ok) {
    const errJson = await response.json().catch(() => null);
    const error = new Error(errJson?.message || errJson?.error || `HTTP ${response.status}`);
    // Carried so the caller can route an insufficient-balance refusal to the
    // credit modal instead of showing it as a generic failure.
    error.data = errJson;
    throw error;
  }
  return response.json();
}

// Katalog bir oturumda değişmez; her modal açılışında yeniden indirmek,
// kullanıcıyı menüyü görmek için bekletmek demek.
let catalogPromise = null;

export function fetchInteractiveCatalog() {
  if (!catalogPromise) {
    catalogPromise = fetch(`${apiBase()}api/interactive/types`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .catch((err) => {
        // Bir sonraki deneme yeniden istesin: kalıcı olarak reddedilmiş bir
        // promise'i önbellekte tutmak, geçici bir ağ hatasını kalıcı bir
        // "menü açılmıyor"a çevirir.
        catalogPromise = null;
        throw err;
      });
  }
  return catalogPromise;
}

/**
 * Üretimi başlat ve worker'ı bekle.
 *
 * 35 dakika, kuyruğun kendi zaman aşımıyla AYNI: yüksek akıl yürütmeli bir
 * yazım ve statik kontrol reddederse bir tamir turu (çağrı başına 15 dk
 * tavan). Burada daha kısa beklemek, hâlâ çalışan — ve hâlâ ödenen — bir işi
 * terk etmek olurdu: iş biter, kutu üretilir, ama kimse onu beklemediği için
 * kullanıcı hata görür.
 */
export async function generateInteractive(fields) {
  const { jobId } = await post("generate", fields);
  if (!jobId) {
    throw new Error("no-job");
  }
  const result = await waitForJob(jobId, { timeoutMs: 2_100_000 });
  return { ...result, jobId };
}

/** What this click will cost, and what the user has left to spend. */
export async function estimateInteractive(fields) {
  return post("generate-credit", fields);
}

/**
 * "AI ile düzenle" — kutunun üstündeki prompt alanı.
 *
 * Üretimle aynı iş sınıfı (bir yazım + gerekirse tamir turu), aynı kuyruk,
 * aynı bekleme süresi. Gönderilen ITEM, blob'daki üretim anı kopyası değil
 * EKRANDAKİ hâli: yazar mini editörde bir kartın yazısını düzelttiyse,
 * AI düzenlemesi o düzeltmenin üzerine çalışmalı, onu sessizce geri almamalı.
 */
export async function editInteractive(fields) {
  const { jobId } = await post("edit", {
    ...fields,
    currentItem: fields.item ? JSON.stringify(fields.item) : undefined,
  });
  if (!jobId) {
    throw new Error("no-job");
  }
  const result = await waitForJob(jobId, { timeoutMs: 2_100_000 });
  return { ...result, jobId };
}

/**
 * "HTML indir" — aktiviteyi tek dosya olarak al, başka bir uygulamada kullan.
 *
 * Bedava ve anlık: model çalışmıyor, kuyruk yok. Sunucu var olan kodu alıp
 * EKRANDAKİ veriyle birleştiriyor.
 *
 * Depodaki dosyanın adresini verip indirtmek daha kısa olurdu ama YANLIŞ
 * dosyayı indirtirdi: o dosya üretim anının verisini taşıyor ve studio'da
 * gerçek veri kutunun state'inden geliyor. Dışarıda state yok — yazar kendi
 * düzeltmelerinin hiçbirini içermeyen bir dosya indirir, üstelik studio'da
 * doğru görünmeye devam ettiği için fark edemezdi.
 *
 * Dosya `fetch` ile alınıp kaydediliyor, düz bir bağlantıyla değil: rota
 * oturum ister ve bir `<a href>` Bearer başlığı taşıyamaz.
 */
export async function downloadInteractive(fields) {
  const response = await fetch(`${apiBase()}api/interactive/download`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: buildForm({
      typeId: fields.typeId,
      sourceBlobName: fields.sourceBlobName,
      contentLanguage: fields.contentLanguage,
      currentItem: fields.item ? JSON.stringify(fields.item) : undefined,
    }),
  });
  if (!response.ok) {
    const errJson = await response.json().catch(() => null);
    throw new Error(errJson?.message || `HTTP ${response.status}`);
  }
  saveAs(await response.blob(), `${fields.typeId || "interactive"}.html`);
}

/**
 * Bir tipin örnek widget'ının adresi.
 *
 * Adres tip id'sinden TÜRETİLİR, hiçbir yerde liste tutulmaz. Rota public —
 * önizleme bir iframe'de açılıyor ve iframe Bearer başlığı taşıyamaz.
 *
 * Hangi tiplerin örneği olduğunu katalog söyler (`type.example`); bu yüzden
 * burada varlık kontrolü yok, düğme zaten yalnızca örneği olanlarda çıkıyor.
 */
export function interactiveExampleUrl(typeId) {
  return `${apiBase()}api/interactive/example/${encodeURIComponent(typeId)}`;
}
